import {
  FileText,
  FileCheck,
  Landmark,
  Paperclip,
  FileSignature,
  File,
  Eye,
  Download,
  Trash2,
  Copy,
  ArrowUpDown,
  Loader2,
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { DOCUMENT_TYPE_LABELS, mockClients } from "../mockData";
import type { MockDocument, DocumentType } from "../types";

const typeIcons: Record<DocumentType, typeof FileText> = {
  invoice_inbound: FileText,
  invoice_outbound: FileText,
  contract: FileCheck,
  bank_statement: Landmark,
  annex: Paperclip,
  agreement: FileSignature,
  other: File,
};

const statusColors: Record<string, string> = {
  processed: "bg-green-100 text-green-700 border-green-200",
  processing: "bg-amber-100 text-amber-700 border-amber-200",
  failed: "bg-red-100 text-red-700 border-red-200",
};

export type SortField = "storedFilename" | "documentType" | "documentDate" | "counterpartyName" | "amount" | "status";
export type SortOrder = "asc" | "desc";

interface FileTableProps {
  documents: MockDocument[];
  sortField: SortField;
  sortOrder: SortOrder;
  onSort: (field: SortField) => void;
  onPreview: (id: string) => void;
  onDelete: (id: string) => void;
  onMirror: (id: string) => void;
  page: number;
  pageSize: number;
  total: number;
  onPageChange: (page: number) => void;
}

function SortableHeader({
  label,
  field,
  currentField,
  currentOrder,
  onSort,
}: {
  label: string;
  field: SortField;
  currentField: SortField;
  currentOrder: SortOrder;
  onSort: (field: SortField) => void;
}) {
  return (
    <TableHead
      className="cursor-pointer select-none hover:bg-neutral-50"
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        <ArrowUpDown
          className={`w-3.5 h-3.5 ${currentField === field ? "text-neutral-700" : "text-neutral-300"}`}
        />
        {currentField === field && (
          <span className="text-xs text-neutral-500">
            {currentOrder === "asc" ? "\u2191" : "\u2193"}
          </span>
        )}
      </div>
    </TableHead>
  );
}

export function FileTable({
  documents,
  sortField,
  sortOrder,
  onSort,
  onPreview,
  onDelete,
  onMirror,
  page,
  pageSize,
  total,
  onPageChange,
}: FileTableProps) {
  const totalPages = Math.ceil(total / pageSize);
  const start = (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, total);

  return (
    <div>
      <div className="border rounded-lg">
        <Table>
          <TableHeader>
            <TableRow>
              <SortableHeader label="Name" field="storedFilename" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortableHeader label="Type" field="documentType" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortableHeader label="Date" field="documentDate" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortableHeader label="Counterparty" field="counterpartyName" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortableHeader label="Amount" field="amount" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <SortableHeader label="Status" field="status" currentField={sortField} currentOrder={sortOrder} onSort={onSort} />
              <TableHead className="w-[120px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {documents.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-neutral-500 py-8">
                  No documents found
                </TableCell>
              </TableRow>
            ) : (
              documents.map((doc) => {
                const TypeIcon = typeIcons[doc.documentType];
                const client = mockClients.find((c) => c.id === doc.clientId);
                return (
                  <TableRow key={doc.id} className="hover:bg-neutral-50">
                    <TableCell className="max-w-[250px]">
                      <div className="flex flex-col">
                        <span className="font-medium text-sm truncate">
                          {doc.storedFilename || doc.originalFilename}
                        </span>
                        {doc.storedFilename && doc.storedFilename !== doc.originalFilename && (
                          <span className="text-xs text-neutral-400 truncate">
                            {doc.originalFilename}
                          </span>
                        )}
                        {client && (
                          <span className="text-xs text-neutral-400">{client.name}</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <TypeIcon className="w-4 h-4 text-neutral-500" />
                        <span className="text-sm">
                          {DOCUMENT_TYPE_LABELS[doc.documentType]}
                        </span>
                      </div>
                    </TableCell>
                    <TableCell className="text-sm">{doc.documentDate}</TableCell>
                    <TableCell className="text-sm max-w-[150px] truncate">
                      {doc.counterpartyName || "-"}
                    </TableCell>
                    <TableCell className="text-sm">
                      {doc.amount > 0
                        ? `${doc.amount.toLocaleString("bg-BG", { minimumFractionDigits: 2 })} ${doc.currency}`
                        : "-"}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={`text-xs ${statusColors[doc.status]}`}
                      >
                        {doc.status === "processing" && (
                          <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                        )}
                        {doc.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => onPreview(doc.id)}
                          title="Preview"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Download"
                          onClick={() => {}}
                        >
                          <Download className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          title="Mirror to client"
                          onClick={() => onMirror(doc.id)}
                        >
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                          title="Delete"
                          onClick={() => onDelete(doc.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {total > pageSize && (
        <div className="flex items-center justify-between mt-3">
          <span className="text-sm text-neutral-500">
            Showing {start}-{end} of {total}
          </span>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={page <= 1}
              onClick={() => onPageChange(page - 1)}
            >
              Previous
            </Button>
            <span className="text-sm text-neutral-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              disabled={page >= totalPages}
              onClick={() => onPageChange(page + 1)}
            >
              Next
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
